/**
 * Header — Apodemia.
 * - Placeholder animado del buscador (efecto máquina de escribir).
 * - Panel de búsqueda al enfocar (búsquedas populares + colección destacada)
 *   con predictive search en vivo (Section Rendering → iurop-predictive-search).
 * - Auto-submit de los selects de idioma/moneda.
 * - No envía búsquedas vacías.
 * Portado y simplificado de shopify-atisse-theme. Ver NOTAS §3.3.
 */
(function () {
  'use strict';

  const isScopedSearchQuery = (value) => /^\(title:.* OR tag:.*\)( AND \(title:.* OR tag:.*\))*$/.test(value);

  const escapeSearchTerm = (value) =>
    value
      .replace(/\\/g, '\\\\')
      .replace(/([:()"])/g, '\\$1')
      .replace(/\*/g, '\\*')
      .replace(/^-/, '\\-');

  const buildScopedSearchQuery = (value) => {
    const clean = value.trim();
    if (!clean || isScopedSearchQuery(clean)) return clean;
    return clean
      .split(/\s+/)
      .map((term) => escapeSearchTerm(term))
      .filter(Boolean)
      .map((term) => `(title:${term}* OR tag:${term}*)`)
      .join(' AND ');
  };

  const scopedSearchUrl = (value) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = new URL(root + 'search', window.location.origin);
    url.searchParams.set('q', buildScopedSearchQuery(value));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    return url.pathname + url.search;
  };

  const normalizeSearchLocation = () => {
    const configured = window.Shopify && window.Shopify.routes && window.Shopify.routes.search_url;
    const searchPath = new URL(configured || '/search', window.location.origin).pathname;
    if (window.location.pathname !== searchPath) return false;
    const params = new URLSearchParams(window.location.search);
    const query = (params.get('q') || '').trim();
    const type = params.get('type');
    if (!query || isScopedSearchQuery(query) || (type && type !== 'product')) return false;
    params.set('q', buildScopedSearchQuery(query));
    params.set('type', 'product');
    params.set('options[prefix]', 'none');
    window.location.replace(searchPath + '?' + params.toString());
    return true;
  };

  if (normalizeSearchLocation()) return;

  // ---- Auto-submit de idioma / moneda -------------------------------------
  const setupLocale = () => {
    document.querySelectorAll('[data-locale-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  };

  // ---- Placeholder animado del buscador -----------------------------------
  const setupSearchAnimation = () => {
    const inputs = document.querySelectorAll('[data-search-animated]');
    if (!inputs.length) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    inputs.forEach((input) => {
      const raw = (input.dataset.suggestions || '').trim();
      const phrases = raw ? raw.split('|').map((s) => s.trim()).filter(Boolean) : [];
      if (phrases.length === 0 || prefersReducedMotion) return;

      const base = input.getAttribute('placeholder') || '';
      let phraseIndex = 0;
      let charIndex = 0;
      let deleting = false;
      let timer = null;
      let paused = false;

      const TYPE = 70;
      const ERASE = 40;
      const HOLD = 1600;

      const step = () => {
        if (paused) return;
        const phrase = phrases[phraseIndex];
        if (!deleting) {
          charIndex++;
          input.setAttribute('placeholder', phrase.slice(0, charIndex));
          if (charIndex >= phrase.length) {
            deleting = true;
            timer = window.setTimeout(step, HOLD);
            return;
          }
          timer = window.setTimeout(step, TYPE);
        } else {
          charIndex--;
          input.setAttribute('placeholder', phrase.slice(0, charIndex));
          if (charIndex <= 0) {
            deleting = false;
            phraseIndex = (phraseIndex + 1) % phrases.length;
            timer = window.setTimeout(step, TYPE);
            return;
          }
          timer = window.setTimeout(step, ERASE);
        }
      };

      const start = () => {
        paused = false;
        window.clearTimeout(timer);
        timer = window.setTimeout(step, 800);
      };
      const stop = () => {
        paused = true;
        window.clearTimeout(timer);
      };

      input.addEventListener('focus', () => {
        stop();
        input.setAttribute('placeholder', base);
      });
      input.addEventListener('blur', () => {
        if (!input.value) start();
      });

      start();
    });
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[role="search"]');
    if (!form) return;
    const input = form.querySelector('input[name="q"]');
    if (input && input.value.trim() === '') {
      e.preventDefault();
      input.value = '';
      input.focus();
      return;
    }
    if (!input) return;
    input.value = buildScopedSearchQuery(input.value);
    let prefix = form.querySelector('input[name="options[prefix]"]');
    if (!prefix) {
      prefix = document.createElement('input');
      prefix.type = 'hidden';
      prefix.name = 'options[prefix]';
      form.appendChild(prefix);
    }
    prefix.value = 'none';
  });

  // ---- Panel de búsqueda + predictive en vivo -----------------------------
  const setupSearchPanel = () => {
    const panel = document.querySelector('[data-search-panel]');
    if (!panel) return;
    const html = document.documentElement;
    const panelInput = panel.querySelector('[data-search-panel-input]');
    const clearBtn = panel.querySelector('[data-search-clear]');
    const headerInput = document.querySelector('.apodemia-header__search-input');
    const mobileIcon = document.querySelector('.apodemia-header__icon--search-mobile');

    // Altura disponible del panel = viewport menos su borde superior (header).
    // Con esto el cuerpo puede desbordar y SCROLLEAR (antes se cortaba).
    const sizePanel = () => {
      if (!panel.classList.contains('is-open')) return;
      const top = panel.getBoundingClientRect().top;
      panel.style.maxHeight = Math.max(240, window.innerHeight - top) + 'px';
    };

    const open = () => {
      if (panel.classList.contains('is-open')) return;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      html.classList.add('apodemia-search-open');
      if (headerInput && panelInput && headerInput.value) panelInput.value = headerInput.value;
      window.requestAnimationFrame(sizePanel);
      if (clearBtn && panelInput) clearBtn.hidden = !panelInput.value;
      window.setTimeout(() => {
        if (panelInput) panelInput.focus({ preventScroll: true });
      }, 80);
    };
    window.addEventListener('resize', sizePanel);

    const close = () => {
      if (!panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      html.classList.remove('apodemia-search-open');
    };

    if (headerInput) {
      headerInput.addEventListener('focus', open);
      headerInput.addEventListener('click', open);
    }
    if (mobileIcon) {
      mobileIcon.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    }
    panel.querySelectorAll('[data-search-close]').forEach((btn) => btn.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });

    // Predictive search en vivo (debounce, Section Rendering).
    const live = panel.querySelector('[data-search-live]');
    const featured = panel.querySelector('[data-search-featured]');
    const popular = panel.querySelector('.apodemia-search__popular');
    let liveTimer = null;

    const resetLive = () => {
      if (!live) return;
      live.hidden = true;
      live.innerHTML = '';
      if (featured) featured.hidden = false;
      if (popular) popular.hidden = false;
    };

    const renderLive = async (term) => {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url =
        root +
        'search/suggest?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product,collection&resources[limit]=8&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,tag' +
        '&section_id=iurop-predictive-search';
      try {
        const res = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const results = doc.querySelector('[data-predictive-results]');
        if (!panelInput || panelInput.value.trim() !== term) return; // descarta obsoletas
        if (!results || !live) return;
        live.innerHTML = '';
        live.appendChild(results);
        const allResults = live.querySelector('.apodemia-search__all');
        if (allResults) {
          const currentUrl = new URL(allResults.href, window.location.origin);
          const searchPath = new URL(root + 'search', window.location.origin).pathname;
          if (currentUrl.pathname === searchPath) allResults.href = scopedSearchUrl(term);
        }
        live.hidden = false;
        if (featured) featured.hidden = true;
        if (popular) popular.hidden = true;
      } catch (_) {
        /* sin red: deja el contenido por defecto */
      }
    };

    if (panelInput && live) {
      panelInput.addEventListener('input', () => {
        const term = panelInput.value.trim();
        if (clearBtn) clearBtn.hidden = !panelInput.value;
        window.clearTimeout(liveTimer);
        if (term.length < 1) {
          resetLive();
          return;
        }
        liveTimer = window.setTimeout(() => renderLive(term), 250);
      });
    }

    panel.querySelectorAll('.apodemia-search__popular a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const searchPath = new URL(root + 'search', window.location.origin).pathname;
      if (url.pathname !== searchPath) return;
      const term = url.searchParams.get('q') || link.textContent.trim();
      link.href = scopedSearchUrl(term);
    });

    // Aspa dentro del pill = vaciar (no cierra el panel).
    if (clearBtn && panelInput) {
      clearBtn.addEventListener('click', () => {
        panelInput.value = '';
        clearBtn.hidden = true;
        resetLive();
        panelInput.focus();
      });
    }

    // Cerrar al interactuar fuera (pointerdown, no click: ver atisse §3.3).
    document.addEventListener('pointerdown', (e) => {
      if (!panel.classList.contains('is-open')) return;
      if (e.target.closest('[data-search-panel]')) return;
      if (e.target.closest('.apodemia-header__search')) return;
      if (e.target.closest('.apodemia-header__icon--search-mobile')) return;
      close();
    });
  };

  const boot = () => {
    setupLocale();
    setupSearchAnimation();
    setupSearchPanel();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-header]')) boot();
  });
})();

/* Menú móvil: acordeón megamenu (un padre abierto, subrayado) + imágenes
 * de producto que cambian según el padre abierto. */
(function () {
  'use strict';
  function initMobileMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    if (!menu || menu.dataset.mmenuBound) return;
    menu.dataset.mmenuBound = '1';

    menu.querySelectorAll('[data-mmenu-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-mmenu-toggle');
        var isOpen = btn.classList.contains('is-open');

        // Cierra todos
        menu.querySelectorAll('[data-mmenu-toggle]').forEach(function (b) {
          b.classList.remove('is-open');
          b.setAttribute('aria-expanded', 'false');
        });
        menu.querySelectorAll('[data-mmenu-panel]').forEach(function (p) { p.hidden = true; });
        menu.querySelectorAll('[data-mmenu-feat]').forEach(function (f) { f.hidden = true; });

        // Abre el pulsado (si no estaba abierto)
        if (!isOpen) {
          btn.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          var panel = menu.querySelector('[data-mmenu-panel="' + key + '"]');
          if (panel) panel.hidden = false;
          var feat = menu.querySelector('[data-mmenu-feat="' + key + '"]');
          if (feat) feat.hidden = false;
        }
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileMenu);
  } else {
    initMobileMenu();
  }
  document.addEventListener('shopify:section:load', initMobileMenu);
})();
